import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import SeriesNameModal from "../pages/ApplicationSetting/SeriesNameSetting/SeriesNameModal";
import { SeriesName } from "../models/SeriesName";
import { ApiResponse } from "../models/ApiResponse";
import { KeyValues } from "../models/Items/FilterOptions";
import api from "../services/ApiService";
import { sortByKey } from "../utils/helpers";
import { FIELDS_WITH_DISPLAY_TEXT, FIELD_NAMES } from "../pages/Products/EditItem/Constants";

type CreatedSeriesName = {
    id: number;
    name: string;
};

type UseProductSeriesNameArgs = {
    formik: any;
    selectedSeriesName: any;
    setSelectedDropDownOptions: (value: any) => void;
    dropDownSeriesNames?: KeyValues[];
};

const emptySeriesName = {
    id: 0,
    description: "",
    leagoCode: "",
    pimKey: "",
    jdeCode: null,
    budget: null,
    isActive: true,
};

export const seriesOptionLabel = (option?: { name?: string; description?: string } | null) =>
    (option?.name || option?.description || "").trim();

export const seriesOptionHasLeagoCode = (option?: { name?: string; description?: string; leagoCode?: string | null } | null) => {
    if (!option) {
        return false;
    }
    if (typeof option.leagoCode === "string") {
        return option.leagoCode.trim() !== "";
    }
    return / \([^)]+\)$/.test(seriesOptionLabel(option));
};

const resolveCreatedId = (data: unknown, description: string, seriesNames: SeriesName[]) => {
    const numericId = typeof data === "number" ? data : Number(data);
    if (Number.isFinite(numericId) && numericId > 0) {
        return numericId;
    }

    const matches = seriesNames
        .filter((item) => item.description?.trim().toLowerCase() === description.trim().toLowerCase())
        .sort((a, b) => b.id - a.id);

    return matches[0]?.id;
};

export function useProductSeriesName({
    formik,
    selectedSeriesName,
    setSelectedDropDownOptions,
    dropDownSeriesNames,
}: UseProductSeriesNameArgs) {
    const [seriesNameOptions, setSeriesNameOptions] = useState<KeyValues[] | null>(null);
    const [leagoCodeById, setLeagoCodeById] = useState<Record<string, string> | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [ignoreBackdropClose, setIgnoreBackdropClose] = useState(false);
    const [seriesName, setSeriesName] = useState("");
    const seriesNamesSeeded = useRef(false);
    const seriesNameInputRef = useRef("");
    const seriesNameTypedByUserRef = useRef(false);
    const dismissedSeriesNameRef = useRef("");
    const requestedNewSeriesNameRef = useRef("");
    const pendingProductSubmitRef = useRef(false);
    const seriesNameForPendingSubmitRef = useRef<CreatedSeriesName | null>(null);
    const lockedSeriesRef = useRef<CreatedSeriesName | null>(null);

    const normalizeSeriesName = (value?: string | null) => value?.trim().toLowerCase() ?? "";

    useEffect(() => {
        if (!seriesNamesSeeded.current && dropDownSeriesNames) {
            seriesNamesSeeded.current = true;
            setSeriesNameOptions(dropDownSeriesNames);
        }
    }, [dropDownSeriesNames]);

    useEffect(() => {
        let active = true;
        api.get<ApiResponse<SeriesName[]>>("/Picklist/series-name-list")
            .then((response) => {
                if (!active) {
                    return;
                }
                const leagoCodes: Record<string, string> = {};
                (response?.data || []).forEach((item) => {
                    leagoCodes[String(item.id)] = item.leagoCode?.trim() || "";
                });
                setLeagoCodeById(leagoCodes);
            })
            .catch((error) => {
                console.error("Exception while loading series names", error);
            });
        return () => {
            active = false;
        };
    }, []);

    const optionsWithLeagoCode = (options: KeyValues[]) => {
        if (!leagoCodeById) {
            return options;
        }
        return options.map((option) => {
            const leagoCode = leagoCodeById[String(option.id)];
            return leagoCode === undefined ? option : { ...option, leagoCode };
        });
    };

    const availableSeriesOptions = optionsWithLeagoCode(seriesNameOptions ?? dropDownSeriesNames ?? []);

    const rememberSeriesNameInput = (value: string, reason?: string) => {
        if (reason === "select" || reason === "clear") {
            seriesNameTypedByUserRef.current = false;
            requestedNewSeriesNameRef.current = "";
        }
        if (reason === "reset" && !value?.trim()) {
            return;
        }
        seriesNameInputRef.current = value ?? "";
        if (reason === "input") {
            seriesNameTypedByUserRef.current = true;
            if (
                requestedNewSeriesNameRef.current &&
                normalizeSeriesName(value) !== normalizeSeriesName(requestedNewSeriesNameRef.current)
            ) {
                requestedNewSeriesNameRef.current = "";
            }
        }
        if (reason === "reset") {
            return;
        }
        if (
            seriesNameForPendingSubmitRef.current &&
            normalizeSeriesName(value) !== normalizeSeriesName(seriesNameForPendingSubmitRef.current.name)
        ) {
            seriesNameForPendingSubmitRef.current = null;
        }
        if (
            lockedSeriesRef.current &&
            normalizeSeriesName(value) !== normalizeSeriesName(lockedSeriesRef.current.name)
        ) {
            lockedSeriesRef.current = null;
        }
        if (
            dismissedSeriesNameRef.current &&
            normalizeSeriesName(value) !== dismissedSeriesNameRef.current
        ) {
            dismissedSeriesNameRef.current = "";
        }
    };

    const openAddSeriesName = (name: string, force = false) => {
        const trimmedName = name?.trim();
        if (isOpen || !trimmedName) {
            return;
        }
        if (!force && dismissedSeriesNameRef.current === normalizeSeriesName(trimmedName)) {
            return;
        }
        dismissedSeriesNameRef.current = "";
        requestedNewSeriesNameRef.current = trimmedName;
        setSeriesName(trimmedName);
        if (force) {
            setIgnoreBackdropClose(true);
            window.setTimeout(() => setIgnoreBackdropClose(false), 400);
        }
        setIsOpen(true);
    };

    const applySeriesName = (series: { id?: number | null; name?: string } | undefined) => {
        const hasSeries = !!(series?.id || series?.name);
        lockedSeriesRef.current = series?.id ? { id: series.id, name: series.name || "" } : null;
        formik.setFieldValue(FIELD_NAMES.seriesName, series?.id ?? null);
        formik.setFieldValue(FIELDS_WITH_DISPLAY_TEXT.seriesName, hasSeries ? series?.name : undefined);
        setSelectedDropDownOptions((current) => ({
            ...current,
            seriesName: hasSeries ? { id: series?.id, name: series?.name } : undefined,
        }));
    };

    const closeAddSeriesName = () => {
        pendingProductSubmitRef.current = false;
        const typedName = seriesName?.trim();
        if (typedName) {
            seriesNameInputRef.current = typedName;
            dismissedSeriesNameRef.current = normalizeSeriesName(typedName);
            applySeriesName({ name: typedName });
        }
        setIsOpen(false);
    };

    const resumePendingProductSubmit = () => {
        if (!pendingProductSubmitRef.current) {
            return;
        }
        pendingProductSubmitRef.current = false;
        setTimeout(() => {
            formik.submitForm();
        }, 0);
    };

    const findExistingSeries = (name: string) => {
        const normalized = normalizeSeriesName(name);
        if (!normalized) {
            return undefined;
        }
        return availableSeriesOptions.find((option) =>
            seriesOptionHasLeagoCode(option) &&
            normalizeSeriesName(seriesOptionLabel(option)) === normalized
        );
    };

    const holdProductSubmitForNewSeriesName = () => {
        const typedName = seriesNameInputRef.current.trim();
        if (!typedName) {
            return false;
        }

        if (
            seriesNameForPendingSubmitRef.current &&
            normalizeSeriesName(seriesNameForPendingSubmitRef.current.name) === normalizeSeriesName(typedName)
        ) {
            return false;
        }

        const selectedLabel = seriesOptionLabel(selectedSeriesName);
        const requestedNewSeries = !!requestedNewSeriesNameRef.current.trim()
            && normalizeSeriesName(requestedNewSeriesNameRef.current) === normalizeSeriesName(typedName);
        const waitingToCreateSeries = requestedNewSeries || (
            !selectedSeriesName?.id && normalizeSeriesName(selectedLabel) === normalizeSeriesName(typedName)
        );
        if (waitingToCreateSeries) {
            pendingProductSubmitRef.current = true;
            openAddSeriesName(typedName, true);
            return true;
        }

        const selectedId = selectedSeriesName?.id || formik.values?.seriesName;
        if (selectedId) {
            const selectedOption = availableSeriesOptions.find((option) => String(option.id) === String(selectedId));
            const selectedLabelMatches = !!selectedOption && normalizeSeriesName(seriesOptionLabel(selectedOption)) === normalizeSeriesName(typedName);
            const typedOverSeriesWithoutLeago = seriesNameTypedByUserRef.current && !!selectedOption && !seriesOptionHasLeagoCode(selectedOption);
            if (selectedLabelMatches && !typedOverSeriesWithoutLeago) {
                return false;
            }
        }

        const existingSeries = findExistingSeries(typedName);
        if (existingSeries?.id) {
            const matchedSeries = {
                id: existingSeries.id,
                name: existingSeries.name || existingSeries.description || typedName,
            };
            seriesNameForPendingSubmitRef.current = matchedSeries;
            applySeriesName(matchedSeries);
            setTimeout(() => {
                formik.submitForm();
            }, 0);
            return true;
        }

        pendingProductSubmitRef.current = true;
        openAddSeriesName(typedName, true);
        return true;
    };

    const getSeriesNameFormValues = () => {
        const pendingSeries = seriesNameForPendingSubmitRef.current;
        if (!pendingSeries?.id) {
            return {};
        }
        seriesNameForPendingSubmitRef.current = null;
        return {
            seriesName: pendingSeries.id,
            seriesNameDisplayText: pendingSeries.name,
        };
    };

    const refreshSeriesNameOptions = async (created: CreatedSeriesName) => {
        let options: KeyValues[] = [];
        try {
            const response = await api.get<ApiResponse<{ seriesNames?: KeyValues[] }>>("/Item/active-dropdown-options");
            if (response?.isSuccess && Array.isArray(response.data?.seriesNames)) {
                options = response.data.seriesNames;
            }
        } catch (error) {
            console.error("Exception while refreshing series names", error);
        }

        const base = options.length > 0 ? options : availableSeriesOptions;
        const next = base.some((option) => String(option.id) === String(created.id))
            ? base
            : [...base, { id: created.id, name: created.name }];
        const nextOptions = [...next].sort(sortByKey());
        setSeriesNameOptions(nextOptions);
        return nextOptions;
    };

    const handleSeriesNameCreated = (created: CreatedSeriesName) => {
        requestedNewSeriesNameRef.current = "";
        const selectedSeries = { id: created.id, name: created.name };
        seriesNameForPendingSubmitRef.current = selectedSeries;
        seriesNameInputRef.current = selectedSeries.name;
        applySeriesName(selectedSeries);
        setIsOpen(false);
        resumePendingProductSubmit();
        refreshSeriesNameOptions(created).then((options) => {
            const createdOption = options.find((option) => String(option.id) === String(created.id));
            if (!createdOption) {
                return;
            }
            const refreshedSeries = {
                id: createdOption.id,
                name: createdOption.name || createdOption.description || created.name,
            };
            if (String(refreshedSeries.id) === String(selectedSeries.id) && refreshedSeries.name === selectedSeries.name) {
                return;
            }
            if (normalizeSeriesName(seriesNameInputRef.current) !== normalizeSeriesName(selectedSeries.name)) {
                return;
            }
            seriesNameForPendingSubmitRef.current = refreshedSeries;
            seriesNameInputRef.current = refreshedSeries.name;
            applySeriesName(refreshedSeries);
        });
    };

    return {
        seriesNameOptions: availableSeriesOptions,
        addSeriesNameModalProps: {
            isOpen,
            seriesName,
            ignoreBackdropClose,
            onClose: closeAddSeriesName,
            onCreated: handleSeriesNameCreated,
        },
        openAddSeriesName,
        rememberSeriesNameInput,
        holdProductSubmitForNewSeriesName,
        getSeriesNameFormValues,
        lockedSeriesRef,
    };
}

const AddSeriesNameModal = ({
    isOpen,
    seriesName,
    ignoreBackdropClose = false,
    onClose,
    onCreated,
}: {
    isOpen: boolean;
    seriesName: string;
    ignoreBackdropClose?: boolean;
    onClose: () => void;
    onCreated: (series: CreatedSeriesName) => Promise<void> | void;
}) => {
    const [seriesNameList, setSeriesNameList] = useState<SeriesName[]>([]);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const wasOpen = useRef(false);

    const fetchSeriesNames = async () => {
        const response = await api.get<ApiResponse<SeriesName[]>>("/Picklist/series-name-list");
        setSeriesNameList(response?.data || []);
    };

    const formik = useFormik({
        initialValues: emptySeriesName,
        validationSchema: Yup.object({
            description: Yup.string()
                .trim()
                .min(1, "Series Name must be at least 1 characters")
                .max(100, "Series Name must be at most 100 characters")
                .required("Series Name is required"),
            leagoCode: Yup.string()
                .trim()
                .required("LEAGO Code is required")
                .test("check-leagoCode-duplicate", "LEAGO Code already exists", (value) => {
                    if (!value?.trim()) {
                        return true;
                    }
                    return !seriesNameList.some(
                        (item) => item.leagoCode?.trim().toLowerCase() === value.trim().toLowerCase()
                    );
                }),
            pimKey: Yup.string()
                .trim()
                .nullable()
                .max(100, "Pim Key must be at most 100 characters")
                .test("check-pimkey-duplicate", "Pim Key already exists", (value) => {
                    if (!value?.trim()) {
                        return true;
                    }
                    return !seriesNameList.some(
                        (item) => item.pimKey?.trim().toLowerCase() === value.trim().toLowerCase()
                    );
                }),
            jdeCode: Yup.string()
                .nullable()
                .max(20, "JDE Code must be at most 20 characters")
                .test("check-jdeCode-duplicate", "JDE Code already exists", (value) => {
                    if (!value?.trim()) {
                        return true;
                    }
                    return !seriesNameList.some(
                        (item) => item?.jdeCode?.trim().toLowerCase() === value.trim().toLowerCase()
                    );
                }),
            budget: Yup.number()
                .transform((value, originalValue) => (
                    originalValue === "" || originalValue === null || originalValue === undefined ? null : value
                ))
                .nullable()
                .min(1, "Tooling budget cannot be 0"),
        }),
        onSubmit: async (values) => {
            setDisableSubmit(true);
            const description = values.description?.trim();
            try {
                const response = await api.post<ApiResponse<number>>("/Picklist/series-name", {
                    description,
                    leagoCode: values.leagoCode?.trim(),
                    pimKey: values.pimKey?.trim() || null,
                    jdeCode: values?.jdeCode || null,
                    budget: values?.budget || null,
                    isActive: JSON.parse(`${values.isActive}`),
                });

                if (!response?.isSuccess) {
                    throw new Error(response?.message || "Unable to add series name");
                }

                const responseId = typeof response.data === "number" ? response.data : Number(response.data);
                let createdId = Number.isFinite(responseId) && responseId > 0 ? responseId : 0;
                if (!createdId) {
                    const seriesNamesResponse = await api.get<ApiResponse<SeriesName[]>>("/Picklist/series-name-list");
                    createdId = resolveCreatedId(response.data, description, seriesNamesResponse?.data || []);
                }
                if (!createdId) {
                    throw new Error("Series name was saved, but the new series could not be selected.");
                }

                toast.success(response.message);
                onCreated({ id: createdId, name: description });
                formik.resetForm();
                setDisableSubmit(false);
            } catch (error: any) {
                toast.error(error?.message || "Unable to add series name");
                setDisableSubmit(false);
            }
        },
    });

    useEffect(() => {
        if (isOpen && !wasOpen.current) {
            formik.resetForm();
            formik.setValues({
                ...emptySeriesName,
                description: seriesName,
            });
            setDisableSubmit(false);
            fetchSeriesNames().catch((error) => {
                console.error("Exception while loading series names", error);
            });
        }
        wasOpen.current = isOpen;
    }, [isOpen, seriesName]);

    return (
        <SeriesNameModal
            isOpen={isOpen}
            onClose={onClose}
            formik={formik}
            isButtonDisabled={disableSubmit}
            disableSeriesName
            ignoreBackdropClose={ignoreBackdropClose}
        />
    );
};

export default AddSeriesNameModal;
